import './ProfileTab.scss';
import { PiUser } from 'react-icons/pi';
import { Input, Loader } from '@piggytechng/component-library';
import { formatToNaira, OverideInputStyle } from '../../../../helpers/utils';
import { MdCode, MdEdit, MdLockOpen, MdVerifiedUser, MdViewModule } from 'react-icons/md';
import ModalAction from '../../../../components/Modals/ModalAction';
import ModalVerifyNin from '../../../../components/Modals/ModalVerifyNin';
import ModalEditIdentity from '../../../../components/Modals/ModalEditIdentity';
import ModalExecuteAction from '../../../../components/Modals/ModalExecuteAction';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import useRolePermissions from '../../../../hooks/useRolePermissions';
import useCustomers from '../../../../hooks/useCustomers';
import dayjs from 'dayjs';
import successIcon from '../../../../assets/icons/succesIcon.svg';
import failureIcon from '../../../../assets/icons/failureIcon.svg';
import NoData from '../../../../components/NoData/NoData';
import TransactionDetailsAlt from '../../../../components/NewSideSheet/TransactionDetailsAlt/TransactionDetailsAlt';

const ProfileTab = ({ userData }: any) => {
  const [showActionModal, setShowActionModal] = useState(false);
  const [action, setAction] = useState<'editName' | 'deleteBvn' | 'editEmailAddress' | 'editPhoneNumber'>('editName');
  const [activeSubTab, setActiveSubTab] = useState<'details' | 'activity'>('details');
  const [bvnView, setBvnView] = useState<'grid' | 'json'>('grid');
  const [lastBvnInputView, setLastBvnInputView] = useState<'grid' | 'json'>('grid');
  const [addressVerificationView, setAddressVerificationView] = useState<'grid' | 'json'>('grid');
  const [ninView, setNinView] = useState<'grid' | 'json'>('grid');
  const [showVerifyNin, setShowVerifyNin] = useState(false);
  const [showReleaseNin, setShowReleaseNin] = useState(false);
  const [showEditIdentity, setShowEditIdentity] = useState(false);
  const [identityType, setIdentityType] = useState<'bvn' | 'nin'>('bvn');
  const [previewImageOpen, setPreviewImageOpen] = useState(false);
  const [showActivityDetails, setShowActivityDetails] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<Record<string, any> | null>(null);
  const { restrictActionByRole } = useRolePermissions({});
  const {
    userActivities,
    isLoadingUserActivities,
    isLoadingMoreUserActivities,
    hasMoreUserActivities,
    loadMoreUserActivities,
    releaseUserNin,
    isRunningUserAction,
  } = useCustomers();
  const params = useParams();
  const userIdIn = params.id as string;

  const isCredit = (activity: any) => {
    if (typeof activity?.apa_action === 'string') return activity.apa_action.toLowerCase() === 'credit';
    return Number(activity?.apa_value) >= 0;
  };

  const handleShowActivityDetails = (activity: any) => {
    const fallbackTitle = [activity?.apa_type, activity?.apa_action]
      .filter(Boolean)
      .map((s: string) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(' ');

    setSelectedActivity({
      title: activity?.apa_text || fallbackTitle || '-----',
      reference: activity?.apa_uniqueid,
      userId: activity?.apa_userid,
      type: activity?.apa_type ? String(activity.apa_type).toUpperCase() : '',
      action: activity?.apa_action ? String(activity.apa_action).toUpperCase() : '',
      value: activity?.apa_value,
      date: activity?.apa_date,
    });
    setShowActivityDetails(true);
  };
  function convertTier(tier: any): string | number | undefined {
    console.log(tier);
    throw new Error('Function not implemented.');
  }

  const formatString = (str: string) => {
    return str.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  };
  const capitalizeString = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const modalTitle = useMemo(() => {
    switch (action) {
      case 'editName':
        return 'Update Full Name';
      case 'deleteBvn':
        return 'Update BVN';
      case 'editEmailAddress':
        return 'Update Email';
      case 'editPhoneNumber':
        return 'Update Phone Number';
      default:
        return '';
    }
  }, [action]);

  const defaultData = useMemo(() => {
    switch (action) {
      case 'editName':
        return { firstName: userData?.firstName, lastName: userData?.lastName };
      case 'deleteBvn':
        return { value: userData?.user_bvn || '' };
      case 'editEmailAddress':
        return { value: userData?.email || '' };
      case 'editPhoneNumber':
        return { value: userData?.phone || '' };
      default:
        return {};
    }
  }, [action, userData]);

  const handleEditAction = (action: 'editName' | 'deleteBvn' | 'editEmailAddress' | 'editPhoneNumber') => {
    if (restrictActionByRole('customerPage', action)) return;
    setAction(action);
    setShowActionModal(true);
  };

  const handleVerifyNin = () => {
    if (restrictActionByRole('customerPage', 'verifyNin')) return;
    setShowVerifyNin(true);
  };

  const handleReleaseNin = () => {
    if (restrictActionByRole('customerPage', 'releaseNin')) return;
    setShowReleaseNin(true);
  };

  const handleEditIdentity = (type: 'bvn' | 'nin') => {
    if (restrictActionByRole('customerPage', type === 'bvn' ? 'editBvn' : 'editNin')) return;
    setIdentityType(type);
    setShowEditIdentity(true);
  };

  const currentIdentityValue = useMemo(() => {
    const raw =
      identityType === 'bvn'
        ? (userData?.bvn?.data?.id ?? userData?.user_bvn)
        : (userData?.nin?.nin ?? userData?.nin?.id);
    return raw ? String(raw) : '';
  }, [identityType, userData]);

  const ninEntries = useMemo<Array<[string, string]>>(() => {
    const raw = userData?.nin;
    if (!raw || typeof raw !== 'object') return [];
    return Object.entries(raw)
      .filter(([, value]) => value !== null && value !== undefined && value !== '' && typeof value !== 'object')
      .map(([key, value]) => [formatString(capitalizeString(key)), String(value)] as [string, string]);
  }, [userData]);

  const bvnEntries = useMemo<Array<[string, string]>>(() => {
    const raw = userData?.bvn?.data;
    if (!raw || typeof raw !== 'object') return [];
    return Object.entries(raw)
      .filter(([, value]) => value !== null && value !== undefined && value !== '' && typeof value !== 'object')
      .map(([key, value]) => [formatString(capitalizeString(key)), String(value)] as [string, string]);
  }, [userData]);

  const lastBvnInputEntries = useMemo<Array<[string, string]>>(() => {
    const raw = userData?.lastBvnInput;
    if (!raw || typeof raw !== 'object') return [];

    const entries: Array<[string, string]> = [];

    Object.entries(raw).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') return;
      if (key === 'date' && typeof value === 'number') {
        entries.push([formatString(capitalizeString(key)), new Date(value * 1000).toLocaleString()]);
      } else if (key === 'data' && typeof value === 'object') {
        const nested = value as Record<string, any>;
        Object.entries(nested).forEach(([nk, nv]) => {
          if (nv === null || nv === undefined || nv === '') return;
          if (nk === 'data' && typeof nv === 'object') {
            Object.entries(nv as Record<string, any>).forEach(([dk, dv]) => {
              if (dv === null || dv === undefined || dv === '') return;
              if (typeof dv !== 'object') {
                entries.push([formatString(capitalizeString(dk)), String(dv)]);
              }
            });
          } else if (typeof nv !== 'object') {
            entries.push([formatString(capitalizeString(nk)), String(nv)]);
          }
        });
      } else if (typeof value !== 'object') {
        entries.push([formatString(capitalizeString(key)), String(value)]);
      }
    });

    return entries;
  }, [userData]);

  const addressVerification = useMemo(() => {
    const raw = userData?.addressVerification;
    if (raw && typeof raw === 'object') return raw;
    return null;
  }, [userData]);

  const addressVerificationEntries = useMemo<Array<[string, string]>>(() => {
    if (!addressVerification) return [];

    return Object.entries(addressVerification)
      .filter(([, value]) => value !== null && value !== undefined && value !== '' && typeof value !== 'object')
      .map(([key, value]) => [formatString(capitalizeString(key)), String(value)] as [string, string]);
  }, [addressVerification]);

  return (
    <>
      <div className="profile-tab__navs">
        <div
          className={`profile-tab__navs__item ${activeSubTab === 'details' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('details')}
        >
          Personal Info
        </div>
        <div
          className={`profile-tab__navs__item ${activeSubTab === 'activity' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('activity')}
        >
          Recent Activity
        </div>
      </div>

      {activeSubTab === 'details' && (
        <div className="user-page">
          <div className="user-page__information">
            <div className="user-page__information-avi">
              {userData?.bvn?.data?.fileName ? (
                <button
                  type="button"
                  className="user-page__information-avi-button"
                  onClick={() => setPreviewImageOpen(true)}
                  title="View full image"
                >
                  <img
                    src={userData?.bvn?.data?.fileName}
                    alt={`${userData?.firstName ?? ''} ${userData?.lastName ?? ''}`.trim()}
                  />
                </button>
              ) : (
                <div className="user-page__information-avatar user-page__information-avatar-placeholder">
                  <PiUser />
                </div>
              )}
            </div>

            <div className="user-page__information-form-container">
              <form className="user-page__information-form" action="">
                <div className="input-cont flex--4">
                  <Input
                    {...OverideInputStyle}
                    label="Full Name"
                    disabled
                    className="user-form-input"
                    value={`${userData?.firstName} ${userData?.lastName}`}
                  />
                  <MdEdit className="icon" onClick={() => handleEditAction('editName')} />
                </div>

                {userData?.tier && (
                  <Input label="Tier" className="user-form-input" disabled value={convertTier(userData?.tier)} />
                )}

                <div className="input-cont flex--4">
                  <Input
                    {...OverideInputStyle}
                    label="BVN"
                    disabled
                    className="user-form-input"
                    placeholder="enter user BVN"
                    value={userData?.user_bvn || 'None'}
                  />
                  <MdEdit className="icon" onClick={() => handleEditAction('deleteBvn')} />
                </div>

                <div className="input-cont flex--4">
                  <Input
                    {...OverideInputStyle}
                    label="Email"
                    className="user-form-input"
                    placeholder="enter user email"
                    value={userData?.email}
                  />
                  <MdEdit className="icon" onClick={() => handleEditAction('editEmailAddress')} />
                </div>

                <div className="input-cont flex--4">
                  <Input
                    {...OverideInputStyle}
                    label="Phone Number"
                    className="user-form-input"
                    disabled
                    placeholder="enter user phone number"
                    value={userData?.phone}
                  />
                  <MdEdit className="icon" onClick={() => handleEditAction('editPhoneNumber')} />
                </div>

                <Input
                  {...OverideInputStyle}
                  label="Joined Date"
                  className="user-form-input"
                  disabled
                  value={new Date(Number(userData?.joined) * 1000).toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                />

                <Input
                  {...OverideInputStyle}
                  label="Date of Birth"
                  className="user-form-input"
                  disabled
                  value={userData?.bvn?.data?.dob || ''}
                />

                {userData?.occupation && (
                  <Input
                    {...OverideInputStyle}
                    label="Occupation"
                    className="user-form-input"
                    disabled
                    value={userData?.occupation}
                  />
                )}

                {userData?.address?.length > 0 &&
                  userData.address.map((item: any, index: number) => (
                    <Input
                      {...OverideInputStyle}
                      key={index}
                      label={formatString(capitalizeString(item.key))}
                      className="user-form-input"
                      disabled
                      value={item.value}
                    />
                  ))}

                {userData?.flags?.length > 0 && (
                  <div className="user-page__information-flags-container">
                    <span className="user-page__information-flags-container-title">User Flags</span>
                    <div className="user-page__information-flags-container-flags">
                      {userData.flags.map((flag: any, index: number) => (
                        <span key={index} className="user-page__information-flags-container-flags-flag">
                          {flag.toLowerCase()}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </form>
              {bvnEntries.length > 0 && (
                <div className="user-page__information-bvn-meta">
                  <div className="bvn-meta-header">
                    <span className="bvn-meta-title">BVN Data</span>
                    <div className="bvn-meta-actions">
                      <button
                        type="button"
                        className="bvn-meta-toggle bvn-meta-toggle--primary"
                        onClick={() => handleEditIdentity('bvn')}
                        title="Edit this customer's BVN"
                      >
                        <MdEdit size={16} />
                        <span>Edit BVN</span>
                      </button>
                      <button
                        type="button"
                        className="bvn-meta-toggle"
                        onClick={() => setBvnView(bvnView === 'grid' ? 'json' : 'grid')}
                        title={bvnView === 'grid' ? 'View raw JSON' : 'View formatted'}
                      >
                        {bvnView === 'grid' ? <MdCode size={16} /> : <MdViewModule size={16} />}
                        <span>{bvnView === 'grid' ? 'JSON' : 'Formatted'}</span>
                      </button>
                    </div>
                  </div>

                  {bvnView === 'grid' ? (
                    <div className="bvn-meta-grid">
                      {bvnEntries.map(([label, value]) => (
                        <div key={label} className="bvn-meta-item">
                          <span className="bvn-meta-item-label">{label}</span>
                          <span className="bvn-meta-item-value">{value}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <pre className="bvn-meta-json">{JSON.stringify(userData?.bvn?.data, null, 2)}</pre>
                  )}
                </div>
              )}

              {lastBvnInputEntries.length > 0 && (
                <div className="user-page__information-bvn-meta">
                  <div className="bvn-meta-header">
                    <span className="bvn-meta-title">Last BVN Input</span>
                    <button
                      type="button"
                      className="bvn-meta-toggle"
                      onClick={() => setLastBvnInputView(lastBvnInputView === 'grid' ? 'json' : 'grid')}
                      title={lastBvnInputView === 'grid' ? 'View raw JSON' : 'View formatted'}
                    >
                      {lastBvnInputView === 'grid' ? <MdCode size={16} /> : <MdViewModule size={16} />}
                      <span>{lastBvnInputView === 'grid' ? 'JSON' : 'Formatted'}</span>
                    </button>
                  </div>

                  {lastBvnInputView === 'grid' ? (
                    <div className="bvn-meta-grid">
                      {lastBvnInputEntries.map(([label, value]) => (
                        <div key={label} className="bvn-meta-item">
                          <span className="bvn-meta-item-label">{label}</span>
                          <span className="bvn-meta-item-value">{value}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <pre className="bvn-meta-json">{JSON.stringify(userData?.lastBvnInput, null, 2)}</pre>
                  )}
                </div>
              )}

              <div className="user-page__information-bvn-meta">
                <div className="bvn-meta-header">
                  <span className="bvn-meta-title">Address Verification</span>
                  {addressVerificationEntries.length > 0 && (
                    <button
                      type="button"
                      className="bvn-meta-toggle"
                      onClick={() => setAddressVerificationView(addressVerificationView === 'grid' ? 'json' : 'grid')}
                      title={addressVerificationView === 'grid' ? 'View raw JSON' : 'View formatted'}
                    >
                      {addressVerificationView === 'grid' ? <MdCode size={16} /> : <MdViewModule size={16} />}
                      <span>{addressVerificationView === 'grid' ? 'JSON' : 'Formatted'}</span>
                    </button>
                  )}
                </div>

                {addressVerificationEntries.length > 0 ? (
                  addressVerificationView === 'grid' ? (
                    <div className="bvn-meta-grid">
                      {addressVerificationEntries.map(([label, value]) => (
                        <div key={label} className="bvn-meta-item">
                          <span className="bvn-meta-item-label">{label}</span>
                          <span className="bvn-meta-item-value">{value}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <pre className="bvn-meta-json">{JSON.stringify(addressVerification, null, 2)}</pre>
                  )
                ) : (
                  <div className="bvn-meta-empty">No address verification</div>
                )}
              </div>

              <div className="user-page__information-bvn-meta">
                <div className="bvn-meta-header">
                  <span className="bvn-meta-title">NIN</span>
                  <div className="bvn-meta-actions">
                    <button
                      type="button"
                      className="bvn-meta-toggle bvn-meta-toggle--primary"
                      onClick={() => handleEditIdentity('nin')}
                      title="Edit this customer's NIN"
                    >
                      <MdEdit size={16} />
                      <span>Edit NIN</span>
                    </button>
                    <button
                      type="button"
                      className="bvn-meta-toggle bvn-meta-toggle--primary"
                      onClick={handleVerifyNin}
                      title="Verify this customer's NIN"
                    >
                      <MdVerifiedUser size={16} />
                      <span>Verify NIN</span>
                    </button>
                    <button
                      type="button"
                      className="bvn-meta-toggle bvn-meta-toggle--primary"
                      onClick={handleReleaseNin}
                      title="Release this customer's NIN to its owner"
                    >
                      <MdLockOpen size={16} />
                      <span>Release NIN</span>
                    </button>
                    {ninEntries.length > 0 && (
                      <button
                        type="button"
                        className="bvn-meta-toggle"
                        onClick={() => setNinView(ninView === 'grid' ? 'json' : 'grid')}
                        title={ninView === 'grid' ? 'View raw JSON' : 'View formatted'}
                      >
                        {ninView === 'grid' ? <MdCode size={16} /> : <MdViewModule size={16} />}
                        <span>{ninView === 'grid' ? 'JSON' : 'Formatted'}</span>
                      </button>
                    )}
                  </div>
                </div>

                {ninEntries.length > 0 ? (
                  ninView === 'grid' ? (
                    <div className="bvn-meta-grid">
                      {ninEntries.map(([label, value]) => (
                        <div key={label} className="bvn-meta-item">
                          <span className="bvn-meta-item-label">{label}</span>
                          <span className="bvn-meta-item-value">{value}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <pre className="bvn-meta-json">{JSON.stringify(userData?.nin, null, 2)}</pre>
                  )
                ) : (
                  <div className="bvn-meta-empty bvn-meta-empty--with-action">
                    <span>No NIN details</span>
                    <div className="bvn-meta-actions">
                      <button
                        type="button"
                        className="bvn-meta-toggle bvn-meta-toggle--primary"
                        onClick={() => handleEditIdentity('nin')}
                      >
                        <MdEdit size={16} />
                        <span>Edit NIN</span>
                      </button>
                      <button
                        type="button"
                        className="bvn-meta-toggle bvn-meta-toggle--primary"
                        onClick={handleVerifyNin}
                      >
                        <MdVerifiedUser size={16} />
                        <span>Verify NIN</span>
                      </button>
                      <button
                        type="button"
                        className="bvn-meta-toggle bvn-meta-toggle--primary"
                        onClick={handleReleaseNin}
                      >
                        <MdLockOpen size={16} />
                        <span>Release NIN</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeSubTab === 'activity' && (
        <div className="wallets-details">
          <h1 className="heading">Recent Activity</h1>

          {isLoadingUserActivities ? (
            <div className="page-loader">
              <Loader color="#0F60D6" />
            </div>
          ) : userActivities?.length ? (
            <>
              <div className="table-container piggypoints user-activities">
                <table className="table">
                  <thead className="table_head">
                    <tr>
                      <th className="icon">{''}</th>
                      <th>Type</th>
                      <th>Reference</th>
                      <th>Amount</th>
                      <th className="memo">Memo</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {userActivities.map((activity: any) => (
                      <tr
                        key={activity.apa_id}
                        className="table-row"
                        onClick={() => handleShowActivityDetails(activity)}
                      >
                        <td className="icon">
                          <img
                            src={isCredit(activity) ? successIcon : failureIcon}
                            alt={isCredit(activity) ? 'credit' : 'debit'}
                          />
                        </td>
                        <td>{activity.apa_type ? String(activity.apa_type).toUpperCase() : '-----'}</td>
                        <td>{activity.apa_uniqueid || '-----'}</td>
                        <td>{activity.apa_value !== undefined ? formatToNaira(activity.apa_value) : '-----'}</td>
                        <td className="memo">{activity.apa_text || '-----'}</td>
                        <td className="date">
                          {activity.apa_date ? dayjs(activity.apa_date * 1000).format('D MMM YYYY | h:mm A') : '-----'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {hasMoreUserActivities && (
                <div className="load-more" onClick={loadMoreUserActivities}>
                  {isLoadingMoreUserActivities ? (
                    <Loader color="#0F60D6" maxWidth="50px" />
                  ) : (
                    <span className="pointer">Show more</span>
                  )}
                </div>
              )}
            </>
          ) : (
            <NoData message="No recent activity at the moment." />
          )}
        </div>
      )}

      <ModalAction
        userId={userIdIn}
        context="user"
        title={modalTitle}
        buttonText="Save Changes"
        modalIsOpen={showActionModal}
        setIsOpen={setShowActionModal}
        data={defaultData}
        action={action}
        actionColor="#1734ca"
      />

      <ModalVerifyNin modalIsOpen={showVerifyNin} setIsOpen={setShowVerifyNin} actionColor="#1734ca" />

      <ModalExecuteAction
        modalIsOpen={showReleaseNin}
        setIsOpen={setShowReleaseNin}
        isLoading={isRunningUserAction}
        actionColor="#1734ca"
        text="release this customer's NIN back to its owner?"
        action={(password: string) => releaseUserNin(password, () => setShowReleaseNin(false))}
      />

      <ModalEditIdentity
        modalIsOpen={showEditIdentity}
        setIsOpen={setShowEditIdentity}
        type={identityType}
        currentValue={currentIdentityValue}
        actionColor="#1734ca"
      />

      <TransactionDetailsAlt
        show={showActivityDetails}
        which="flexnaira"
        id={''}
        data={selectedActivity}
        onClose={() => setShowActivityDetails(false)}
      />

      {previewImageOpen && userData?.bvn?.data?.fileName && (
        <div
          className="image-preview-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setPreviewImageOpen(false)}
        >
          <button
            type="button"
            className="image-preview-close"
            onClick={() => setPreviewImageOpen(false)}
            aria-label="Close image preview"
          >
            &times;
          </button>
          <img
            className="image-preview-image"
            src={userData?.bvn?.data?.fileName}
            alt={`${userData?.firstName ?? ''} ${userData?.lastName ?? ''}`.trim()}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export default ProfileTab;
